import { useState, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingArrow,
  arrow,
  Placement,
  safePolygon
} from '@floating-ui/react';
import styles from './Tooltip.module.scss';
import { classnames } from '../../../utils';

type Props = {
  content: React.ReactNode;
  delay?: number;
  html?: boolean;
  className?: string;
  shouldRender?: boolean;
  placement?: Placement;
  variant?: 'dark' | 'light';
  padding?: 'default' | 'small';
  maxWidth?: number;
  allowHover?: boolean;
};

export default function Tooltip({
  children,
  content,
  html,
  delay = 0,
  className,
  shouldRender = true,
  placement = 'top',
  variant = 'dark',
  padding = 'default',
  maxWidth = 350,
  allowHover = false
}: React.PropsWithChildren<Props>) {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'start'
      }),
      shift(),
      arrow({
        element: arrowRef
      })
    ]
  });

  const hover = useHover(context, {
    move: false,
    restMs: delay,
    handleClose: allowHover ? safePolygon({ requireIntent: false, blockPointerEvents: true }) : undefined
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  if (!content || !shouldRender) {
    return <>{children}</>;
  }

  const contentStyles = { '--tooltip-max-width': `${maxWidth}px` } as React.CSSProperties;

  return (
    <>
      <span className={className || styles.children} ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </span>

      <FloatingPortal>
        {isOpen && (
          <div
            className={classnames([styles.content, styles[variant], styles[`padding${padding}`]])}
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...contentStyles }}
            {...getFloatingProps()}
          >
            {html ? <div dangerouslySetInnerHTML={{ __html: content }} /> : content}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={variant === 'dark' ? 'black' : 'white'}
              strokeWidth={variant === 'dark' ? 0 : 1}
              stroke={variant === 'light' ? '#e8e8e8' : undefined}
            />
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
